import { useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { addPattern } from './environmentFormModel';

interface Props {
  id: string;
  value: string[];
  onChange: (next: string[]) => void;
  /** The text typed but not added yet; the form adds it on save. */
  draft: string;
  onDraftChange: (draft: string) => void;
  invalid?: boolean;
  describedBy?: string;
  disabled?: boolean;
}

/**
 * The chips' state: Enter, comma or Tab adds the typed pattern (`addPattern`: the API's rules, its reason shows under
 * the field), Backspace on an empty input removes the last one, leaving the input adds what is typed.
 */
function usePatternInput({ value, onChange, draft, onDraftChange }: Props) {
  const [problem, setProblem] = useState<string | undefined>();
  const add = (): boolean => {
    const result = addPattern(value, draft);
    if (result.kind === 'empty') return false;
    if (result.kind === 'rejected') {
      setProblem(result.message);
      return false;
    }
    onChange(result.list);
    onDraftChange('');
    setProblem(undefined);
    return true;
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || (e.key === 'Tab' && draft.trim())) {
      e.preventDefault();
      add();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };
  const onDraft = (text: string) => {
    onDraftChange(text);
    if (problem) setProblem(undefined);
  };
  return { problem, onKeyDown, onDraft, onBlur: () => (draft.trim() ? add() : undefined) };
}

function PatternChip({ pattern, disabled, onRemove }: { pattern: string; disabled?: boolean; onRemove: () => void }) {
  return (
    <span data-testid={`env-pattern-${pattern}`} className="inline-flex max-w-full items-center gap-1 rounded-md border bg-secondary py-0.5 pl-2 pr-0.5 font-mono text-xs text-foreground">
      <span className="truncate">{pattern}</span>
      <button
        type="button"
        disabled={disabled}
        aria-label={`Remove ${pattern}`}
        onClick={onRemove}
        className="flex size-6 items-center justify-center rounded-sm text-subtle outline-none hover:bg-surface-3 hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 max-sm:size-8"
      >
        <Icon name="close" size={14} />
      </button>
    </span>
  );
}

interface TextProps extends Props {
  problem?: string;
  onDraft: (text: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}

function PatternTextInput({ id, value, draft, invalid, describedBy, disabled, problem, onDraft, onKeyDown, onBlur }: TextProps) {
  return (
    <input
      id={id}
      data-testid="env-pattern-input"
      value={draft}
      disabled={disabled}
      onChange={(e) => onDraft(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      placeholder={value.length ? 'Add a pattern' : 'Add a pattern, e.g. main or release/*'}
      spellCheck={false}
      autoComplete="off"
      aria-invalid={invalid || problem ? true : undefined}
      aria-describedby={problem ? `${id}-problem` : describedBy}
      className="h-8 min-w-[10ch] flex-1 bg-transparent px-1 font-mono text-base text-foreground outline-none placeholder:text-subtle sm:text-[13px]"
    />
  );
}

/**
 * Allowed branch patterns as mono chips with an input (design 28): Enter, comma or Tab adds the typed pattern, × or
 * Backspace on an empty input removes one. A pattern this console can already tell is wrong (`branchPatternProblem`
 * from @hangar/shared: `*` is the only wildcard, a branch name not a ref, 100 characters) is not added; its reason
 * shows under the field, like the API's.
 */
export function BranchPatternInput(props: Props) {
  const { id, value, onChange, invalid, disabled } = props;
  const { problem, onKeyDown, onDraft, onBlur } = usePatternInput(props);
  return (
    <div className="flex flex-col gap-1.5">
      <div
        data-testid="env-patterns"
        className={cn(
          'flex min-h-11 flex-wrap items-center gap-1.5 rounded-lg border border-input bg-background px-2 py-1.5 transition-[border-color,box-shadow] duration-150 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/30',
          (invalid || problem) && 'border-destructive ring-destructive/20',
          disabled && 'opacity-60',
        )}
      >
        {value.map((p) => (
          <PatternChip key={p} pattern={p} disabled={disabled} onRemove={() => onChange(value.filter((x) => x !== p))} />
        ))}
        <PatternTextInput {...props} problem={problem} onDraft={onDraft} onKeyDown={onKeyDown} onBlur={onBlur} />
      </div>
      {problem ? (
        <p id={`${id}-problem`} role="alert" className="flex items-start gap-1 text-xs text-destructive">
          <Icon name="error" size={14} className="mt-px" />
          <span className="min-w-0 break-words">{problem}</span>
        </p>
      ) : null}
    </div>
  );
}
